/*
 * ESC, ONE LAYER AT A TIME.
 *
 * The record opens inside other pages (Leads, Follow-ups, Approvals), and each of
 * them used to bind its own window-wide Esc that closed the whole panel from
 * anywhere, including the composer's textarea, which threw away an edited draft
 * (2026-10-01). Now everything Esc can close registers here as a layer, and one
 * press closes the most recently opened one: the open menu before the correction
 * form, the correction form before the panel.
 *
 * A key pressed while typing (a text input, a textarea, a select, anything
 * contenteditable) is never Esc for a layer. The field owns it, and a field that
 * wants Esc for itself (the country editor's cancel) handles it on its own
 * onKeyDown.
 *
 * Pure, so the rules are tested without a browser. useEscape.js binds it.
 */

// Inputs that take no typing: Esc on a focused checkbox still means "close".
const TEXTLESS = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

/** Is this key going into something the user is typing in? */
export function typingIn(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = String(target.tagName || "").toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  if (tag !== "input") return false;
  return !TEXTLESS.has(String(target.type || "text").toLowerCase());
}

export function createLayers() {
  const stack = [];
  return {
    /** Adds a layer on top. Returns the function that takes it off again. */
    open(onEscape) {
      const entry = { onEscape };
      stack.push(entry);
      return () => {
        const i = stack.indexOf(entry);
        if (i >= 0) stack.splice(i, 1);
      };
    },
    /** Closes the top layer if this keypress is an Esc meant for one; true if it was. */
    handle(event) {
      if (!event || event.key !== "Escape" || event.defaultPrevented) return false;
      if (event.metaKey || event.ctrlKey || event.altKey) return false;
      if (typingIn(event.target)) return false;
      const top = stack[stack.length - 1];
      if (!top) return false;
      if (typeof event.preventDefault === "function") event.preventDefault();
      top.onEscape();
      return true;
    },
    get size() {
      return stack.length;
    },
  };
}

// The app's one stack.
export const layers = createLayers();
